/**
 * Phase 3a §4 — Employer portal at `/portal/employer/:employerId`.
 *
 * Renders one of four phases returned by the dashboard endpoint:
 *   - incomplete                 compact "Continue your application" card
 *   - submitted_pre_enrollment   timeline + secondary action links
 *   - enrollment_open            hero + operations strip; hero downgrades
 *                                to a tile when the roster isn't empty
 *   - steady_state               full dashboard with 5 stats tiles
 *
 * Per the brief, phase detection is server-side (10s cache at most); we
 * just render what `/api/portal/employer/{id}/dashboard` returns.
 *
 * "Total coverage approved" shows "Pending underwriting" when the value is
 * zero AND at least one application is complete. Otherwise formatted USD.
 */
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import PortalChrome from "../components/portal/PortalChrome";
import TeamAccessCard from "../components/portal/TeamAccessCard";
import { employerMe, portalEmployerDashboard } from "../lib/api";

import "./PortalEmployer.css";

/* Format USD cents — always show two decimals per demo-polish rule. */
function fmtUSD(cents) {
    if (cents == null) return "—";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(cents / 100);
}

function fmtDate(iso, locale = "en") {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(locale === "es" ? "es-US" : "en-US", {
        year: "numeric", month: "long", day: "numeric",
    });
}


/* ----- Phase renderers ---------------------------------------------------- */

function PhaseIncomplete({ t, data }) {
    const step = data.application_current_step || 1;
    const total = data.context?.total_steps || 12;
    const pct = Math.round((step / total) * 100);
    return (
        <section className="phe-incomplete" data-testid="phe-incomplete">
            <div className="phe-eyebrow mono">{t("portal.employer.phases.incomplete.eyebrow")}</div>
            <h1 className="phe-h1">{t("portal.employer.phases.incomplete.title")}</h1>
            <p className="phe-sub">{t("portal.employer.phases.incomplete.sub")}</p>

            <div className="phe-progress-card" data-testid="phe-progress">
                <div className="phe-progress-head mono">
                    {t("portal.employer.phases.incomplete.stepOf", { n: step, total })}
                </div>
                <div className="phe-progress-bar"><div className="phe-progress-fill" style={{ width: `${pct}%` }} /></div>
                <a
                    href={`/team/apply?step=${step}`}
                    className="phe-btn phe-btn-primary"
                    data-testid="phe-continue-cta"
                >
                    {t("portal.employer.cta.continueApplication")} →
                </a>
            </div>
        </section>
    );
}

function PhaseSubmitted({ t, data }) {
    return (
        <section className="phe-submitted" data-testid="phe-submitted">
            <div className="phe-eyebrow mono">{t("portal.employer.phases.submitted.eyebrow")}</div>
            <h1 className="phe-h1">{t("portal.employer.phases.submitted.title")}</h1>
            <p className="phe-sub">{t("portal.employer.phases.submitted.sub")}</p>

            <div className="phe-status-card" data-testid="phe-status-card">
                <dl>
                    <div>
                        <dt className="mono">{t("portal.employer.phases.submitted.submittedOn")}</dt>
                        <dd>{fmtDate(data.submitted_at)}</dd>
                    </div>
                    <div>
                        <dt className="mono">{t("portal.employer.phases.submitted.expectedDecision")}</dt>
                        <dd>
                            {t("portal.employer.phases.submitted.expectedDays", { n: data.context?.expected_decision_days ?? 14 })}
                        </dd>
                    </div>
                    <div>
                        <dt className="mono">{t("portal.employer.phases.submitted.notify")}</dt>
                        <dd>{t("portal.employer.phases.submitted.notifyBody")}</dd>
                    </div>
                </dl>
            </div>
        </section>
    );
}

function PhaseEnrollmentOpen({ t, data }) {
    const statsStrip = (
        <div className="phe-ops-strip" data-testid="phe-ops-strip">
            <StatTile mono label={t("portal.employer.stats.invited")} value={data.stats.invited} />
            <StatTile mono label={t("portal.employer.stats.inProgress")} value={data.stats.in_progress} />
            <StatTile mono label={t("portal.employer.stats.complete")} value={data.stats.complete} />
        </div>
    );

    const heroCensusCard = (
        <article className="phe-hero-card" data-testid="phe-hero-card">
            <div className="phe-eyebrow mono">{t("portal.employer.phases.enrollOpen.eyebrow")}</div>
            <h1 className="phe-h1">{t("portal.employer.phases.enrollOpen.heroTitle")}</h1>
            <p className="phe-sub phe-hero-body">
                {t("portal.employer.phases.enrollOpen.heroBody")}
            </p>
            <a
                href={`/portal/employer/${data.employer_id}/census`}
                className="phe-btn phe-btn-primary"
                data-testid="phe-upload-census-cta"
            >
                {t("portal.employer.cta.uploadCensus")} →
            </a>
        </article>
    );

    const downgradedTile = (
        <article className="phe-tile" data-testid="phe-update-tile">
            <div className="phe-tile-label mono">{t("portal.employer.phases.enrollOpen.tileLabel")}</div>
            <a
                href={`/portal/employer/${data.employer_id}/census`}
                className="phe-btn phe-btn-secondary"
                data-testid="phe-update-census-cta"
            >
                {t("portal.employer.cta.updateCensus")} →
            </a>
        </article>
    );

    // Empty roster: hero primary, ops strip secondary.
    // Non-empty: ops strip primary, census becomes a tile.
    if (data.roster_empty) {
        return (
            <section className="phe-enroll-empty">
                {heroCensusCard}
                {statsStrip}
            </section>
        );
    }
    return (
        <section className="phe-enroll-active">
            {statsStrip}
            {downgradedTile}
        </section>
    );
}

function PhaseSteady({ t, data }) {
    const coverageCell = () => {
        const cents = data.stats.coverage_approved_cents;
        const hasComplete = data.stats.complete > 0;
        if (cents > 0) return fmtUSD(cents);
        if (hasComplete) return t("portal.employer.phases.steady.pendingUW");
        return "—";
    };
    return (
        <section className="phe-steady" data-testid="phe-steady">
            <div className="phe-hero-strip" data-testid="phe-hero-strip">
                <h1 className="phe-h1">{data.employer_name}</h1>
                <p className="phe-sub">
                    {data.stats.coverage_effective_date ? (
                        <>
                            {t("portal.employer.phases.steady.coverageEffective")}{" "}
                            <span className="phe-hero-date mono">{fmtDate(data.stats.coverage_effective_date)}</span>
                        </>
                    ) : (
                        t("portal.employer.phases.steady.coverageEffectivePending")
                    )}
                </p>
            </div>

            <div className="phe-tiles" data-testid="phe-steady-tiles">
                <StatTile
                    label={t("portal.employer.stats.coverageEffective")}
                    value={data.stats.coverage_effective_date ? fmtDate(data.stats.coverage_effective_date) : "—"}
                    mono={false}
                />
                <StatTile label={t("portal.employer.stats.invited")} value={data.stats.invited} mono />
                <StatTile label={t("portal.employer.stats.inProgress")} value={data.stats.in_progress} mono />
                <StatTile label={t("portal.employer.stats.complete")} value={data.stats.complete} mono />
                <StatTile
                    label={t("portal.employer.stats.coverageApproved")}
                    value={coverageCell()}
                    mono={false}
                    data-testid="phe-coverage-approved"
                />
            </div>

            {/* Phase 3b §7 — weekly digest subscribe toggle */}
            <DigestSubscribeCard employerId={data.employer_id} t={t} />
        </section>
    );
}

function DigestSubscribeCard({ employerId, t }) {
    const [loading, setLoading] = useState(false);
    const [subscribed, setSubscribed] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                // Read current state from the dashboard's aggregate embed — but
                // the dashboard endpoint doesn't surface it. Do a lightweight
                // toggle-off call that just fetches current state isn't exposed,
                // so we default to "not subscribed" and let the user opt in.
                setSubscribed(false);
            } catch (_) { /* non-blocking */ }
        })();
    }, []);

    const toggle = async () => {
        setLoading(true);
        try {
            const next = !subscribed;
            const { employerDigestSubscribe } = await import("../lib/api");
            await employerDigestSubscribe(employerId, next);
            setSubscribed(next);
        } catch (_) {
            // keep current state
        } finally {
            setLoading(false);
        }
    };

    return (
        <aside className="phe-digest-card" data-testid="phe-digest-card">
            <div className="phe-digest-text">
                <div className="phe-digest-eyebrow mono">
                    {t("portal.employer.digest.label")}
                </div>
                <div className="phe-digest-title">
                    {t("portal.employer.digest.title")}
                </div>
                <p className="phe-digest-sub">{t("portal.employer.digest.sub")}</p>
            </div>
            <button
                type="button"
                className={`phe-digest-btn ${subscribed ? "phe-digest-btn-on" : ""}`}
                onClick={toggle}
                disabled={loading}
                data-testid="phe-digest-toggle"
                aria-pressed={subscribed ? "true" : "false"}
            >
                {loading
                    ? t("portal.employer.digest.saving")
                    : subscribed
                        ? t("portal.employer.digest.subscribed")
                        : t("portal.employer.digest.subscribe")}
            </button>
        </aside>
    );
}

function StatTile({ label, value, mono = false, ...rest }) {
    return (
        <div className="phe-tile phe-stat-tile" {...rest}>
            <div className="phe-tile-label mono">{label}</div>
            <div className={`phe-tile-value ${mono ? "mono" : ""}`}>{value}</div>
        </div>
    );
}


/* ----- Actions strip ------------------------------------------------------ */

function ActionsStrip({ t, data }) {
    const primary = data.primary_cta;
    const secondary = data.secondary_ctas || [];
    return (
        <nav className="phe-actions" aria-label="actions" data-testid="phe-actions">
            {primary && (
                <a
                    href={primary.href}
                    className="phe-btn phe-btn-primary"
                    data-testid={`phe-cta-${primary.key}`}
                >
                    {t(primary.label_key)} →
                </a>
            )}
            {secondary.length > 0 && (
                <ul className="phe-actions-secondary">
                    {secondary.map((cta) => {
                        if (cta.disabled) {
                            return (
                                <li key={cta.key}>
                                    <span
                                        className="phe-link phe-link-disabled"
                                        aria-disabled="true"
                                        role="link"
                                        tabIndex={-1}
                                        data-testid={`phe-cta-${cta.key}`}
                                        data-disabled="true"
                                    >
                                        {t(cta.label_key)}
                                    </span>
                                    {cta.caption_key && (
                                        <span
                                            className="phe-cta-caption mono"
                                            data-testid={`phe-cta-${cta.key}-caption`}
                                        >
                                            {t(cta.caption_key)}
                                        </span>
                                    )}
                                </li>
                            );
                        }
                        return (
                            <li key={cta.key}>
                                <a
                                    href={cta.href}
                                    className="phe-link"
                                    data-testid={`phe-cta-${cta.key}`}
                                    {...(cta.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                                >
                                    {t(cta.label_key)}
                                </a>
                            </li>
                        );
                    })}
                </ul>
            )}
        </nav>
    );
}


/* ----- Page --------------------------------------------------------------- */

export default function PortalEmployer() {
    const { t } = useTranslation();
    const { employerId } = useParams();
    const _nav = useNavigate();
    const [session, setSession] = useState(null);
    const [data, setData] = useState(null);
    const [phase, setPhase] = useState("loading");     // loading | ok | not_logged_in | wrong_org | error
    const [err, setErr] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const me = await employerMe();
                if (!me?.email || !me?.employer_id) {
                    setPhase("not_logged_in");
                    return;
                }
                setSession(me);
                if (me.employer_id !== employerId) {
                    setPhase("wrong_org");
                    return;
                }
                const d = await portalEmployerDashboard(employerId);
                setData(d);
                setPhase("ok");
            } catch (e) {
                const status = e?.response?.status;
                if (status === 401) {
                    setPhase("not_logged_in");
                } else if (status === 403) {
                    setPhase("wrong_org");
                } else {
                    setErr(e?.response?.data?.detail || String(e));
                    setPhase("error");
                }
            }
        })();
    }, [employerId]);

    if (phase === "not_logged_in") {
        return <Navigate to="/employer-signin" replace />;
    }

    const chromeName = session?.employer_name || session?.employer_id || "";

    if (phase === "loading") {
        return (
            <PortalChrome actorType="employer" displayName={chromeName}>
                <div className="phe-wrap phe-centered">
                    <div className="phe-label mono">{t("portal.shared.loading")}</div>
                </div>
            </PortalChrome>
        );
    }

    if (phase === "wrong_org") {
        return (
            <PortalChrome actorType="employer" displayName={chromeName}>
                <div className="phe-wrap" data-testid="phe-wrong-org">
                    <div className="phe-eyebrow mono">403 · Forbidden</div>
                    <h1 className="phe-h1">{t("portal.employer.wrongOrgTitle")}</h1>
                    <p className="phe-sub">
                        {t("portal.employer.wrongOrgBody", { employer: chromeName })}
                    </p>
                </div>
            </PortalChrome>
        );
    }

    if (phase === "error") {
        return (
            <PortalChrome actorType="employer" displayName={chromeName}>
                <div className="phe-wrap" data-testid="phe-error">
                    <div className="phe-eyebrow mono">Error</div>
                    <h1 className="phe-h1">{t("portal.shared.errorTitle")}</h1>
                    <p className="phe-sub">{String(err)}</p>
                </div>
            </PortalChrome>
        );
    }

    // phase === "ok"
    const renderers = {
        incomplete: PhaseIncomplete,
        submitted_pre_enrollment: PhaseSubmitted,
        enrollment_open: PhaseEnrollmentOpen,
        steady_state: PhaseSteady,
    };
    const Render = renderers[data.phase] || PhaseSteady;

    return (
        <PortalChrome actorType="employer" displayName={chromeName}>
            <div
                className={`phe-wrap phe-phase-${data.phase}`}
                data-phase={data.phase}
                data-testid="phe-wrap"
            >
                <Render t={t} data={data} />
                <ActionsStrip t={t} data={data} />
                {data.phase === "steady_state" && (
                    <TeamAccessCard
                        employerId={employerId}
                        employerName={chromeName}
                    />
                )}
            </div>
        </PortalChrome>
    );
}
